import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI, blogAPI, testimonialsAPI, awardsAPI } from '../../utils/api';
import Card from '../../components/common/Card';
import Loading from '../../components/common/Loading';
import { FaImage, FaBlog, FaStar, FaTrophy, FaPlus } from 'react-icons/fa';

const Dashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    blogPosts: 0,
    testimonials: 0,
    awards: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [projects, blog, testimonials, awards] = await Promise.all([
          projectsAPI.getAll(),
          blogAPI.getAll(),
          testimonialsAPI.getAll(),
          awardsAPI.getAll(),
        ]);

        setStats({
          projects: projects.data.data?.length || 0,
          blogPosts: blog.data.data?.length || 0,
          testimonials: testimonials.data.data?.length || 0,
          awards: awards.data.data?.length || 0,
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const quickActions = [
    {
      to: '/admin/projects',
      label: 'New Project',
      icon: FaImage,
      color: 'bg-blue-500',
    },
    {
      to: '/admin/blog',
      label: 'New Blog Post',
      icon: FaBlog,
      color: 'bg-green-500',
    },
    {
      to: '/admin/testimonials',
      label: 'New Testimonial',
      icon: FaStar,
      color: 'bg-yellow-500',
    },
    {
      to: '/admin/awards',
      label: 'New Award',
      icon: FaTrophy,
      color: 'bg-purple-500',
    },
  ];

  const statCards = [
    {
      label: 'Total Projects',
      value: stats.projects,
      icon: FaImage,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      label: 'Blog Posts',
      value: stats.blogPosts,
      icon: FaBlog,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      label: 'Testimonials',
      value: stats.testimonials,
      icon: FaStar,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      label: 'Awards',
      value: stats.awards,
      icon: FaTrophy,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  if (loading) {
    return <Loading text="Loading dashboard..." />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-secondary mb-2">
          Welcome Back!
        </h1>
        <p className="text-text-primary font-body">
          Manage your portfolio content from this dashboard
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-body text-gray-600 mb-1">
                  {stat.label}
                </p>
                <p className="text-3xl font-heading font-bold text-secondary">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <stat.icon className={`text-2xl ${stat.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-2xl font-heading font-semibold text-secondary mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link key={index} to={action.to}>
              <Card
                hover
                className="p-6 flex items-center space-x-4 cursor-pointer"
              >
                <div className={`p-3 rounded-full ${action.color} text-white`}>
                  <FaPlus />
                </div>
                <div>
                  <p className="font-body font-medium text-text-primary">
                    {action.label}
                  </p>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Management Links */}
      <div>
        <h2 className="text-2xl font-heading font-semibold text-secondary mb-4">
          Content Management
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/admin/projects">
            <Card hover className="p-6">
              <div className="flex items-center space-x-4">
                <FaImage className="text-3xl text-blue-600" />
                <div>
                  <h3 className="font-heading font-semibold text-lg text-secondary">
                    Manage Projects
                  </h3>
                  <p className="text-sm font-body text-gray-600">
                    Add, edit, or delete portfolio projects
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/admin/blog">
            <Card hover className="p-6">
              <div className="flex items-center space-x-4">
                <FaBlog className="text-3xl text-green-600" />
                <div>
                  <h3 className="font-heading font-semibold text-lg text-secondary">
                    Manage Blog
                  </h3>
                  <p className="text-sm font-body text-gray-600">
                    Create and publish blog posts
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/admin/testimonials">
            <Card hover className="p-6">
              <div className="flex items-center space-x-4">
                <FaStar className="text-3xl text-yellow-600" />
                <div>
                  <h3 className="font-heading font-semibold text-lg text-secondary">
                    Manage Testimonials
                  </h3>
                  <p className="text-sm font-body text-gray-600">
                    Add client testimonials and reviews
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/admin/awards">
            <Card hover className="p-6">
              <div className="flex items-center space-x-4">
                <FaTrophy className="text-3xl text-purple-600" />
                <div>
                  <h3 className="font-heading font-semibold text-lg text-secondary">
                    Manage Awards
                  </h3>
                  <p className="text-sm font-body text-gray-600">
                    Showcase your achievements
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
